// What each model will actually accept for `reasoning_effort`, learned the only way there is to learn it:
// by being told no. A model that refuses the app's "don't think" tier is worked around in the inference
// layer (one retry, see rejectedReasoningField); this remembers the answer so the NEXT request is a single
// call, and tells the user what changed under them.
//
// Keyed per MODEL, not per provider: behind a gateway one endpoint serves hundreds of models that disagree
// with each other, so a provider-wide setting would be wrong for all but one of them.
import type { ReasoningEffort } from "@aicc/inference";
import { loadJsonSetting, saveJsonSetting } from "./api";

const KEY = "reasoning_fix";
/** model id → what was REJECTED and what to use instead ("off" = send no reasoning fields at all).
 *  `rejected` scopes the fix: it applies only when the user asks for that exact tier again. Keyed per
 *  model AND per rejected tier is what stops a utility call's "none" rejection from silently rewriting
 *  the user's explicit "high" — a tier the model supports — for every later chat. `rejected` undefined
 *  is a legacy entry (pre-scoping) and applies to any request, as it always did. */
export type ReasoningFix = { rejected?: ReasoningEffort; using: ReasoningEffort | "off" };
export type ReasoningFixes = Record<string, ReasoningFix>;

/** Notice for the UI, mirroring TTS_STATUS_EVENT: `detail` is the sentence to show. */
export const REASONING_FIX_EVENT = "reasoning-fix";

// Read once at startup and kept in memory: `effortFor` is consulted while building every request, which
// can't await a settings read. Writes go to both.
let fixes: ReasoningFixes = {};

export async function loadReasoningFixes(): Promise<void> {
  const raw = await loadJsonSetting<Record<string, unknown>>(KEY, {});
  // Legacy shape was a bare tier string. An "off" was only ever recorded from a "none" rejection, so it
  // migrates fully scoped; a bare tier's rejected request wasn't stored — it stays unscoped (old behavior).
  fixes = Object.fromEntries(Object.entries(raw).map(([m, v]) => [
    m,
    typeof v === "string"
      ? ({ rejected: v === "off" ? ("none" as ReasoningEffort) : undefined, using: v as ReasoningEffort | "off" })
      : (v as ReasoningFix),
  ]));
}

/** The learned substitute for a model when THIS tier is requested; undefined = send what was asked. */
export const reasoningFixFor = (model: string, requested?: ReasoningEffort): ReasoningEffort | "off" | undefined => {
  const f = fixes[model];
  if (!f) return undefined;
  return f.rejected === undefined || f.rejected === requested ? f.using : undefined;
};

/** Record what a model turned out to accept, and say so once. Re-recording the same answer is silent —
 *  otherwise every utility call on that model would re-announce it. */
export function noteReasoningFix(model: string, using: ReasoningEffort | "off", requested?: ReasoningEffort): void {
  if (!model || (fixes[model]?.using === using && fixes[model]?.rejected === requested)) return;
  fixes = { ...fixes, [model]: { rejected: requested, using } };
  void saveJsonSetting(KEY, fixes);
  const what = using === "off"
    ? "so thinking control has been turned off for it"
    : `so it's been set to “${using}” for that model`;
  window.dispatchEvent(new CustomEvent(REASONING_FIX_EVENT, {
    detail: `${model} rejects reasoning effort “${requested ?? "none"}” — ${what}. Review or clear it under Settings → Generation → Learned fixes.`,
  }));
}

/** Testing seam / a way to clear a stale answer after a model changes what it accepts. */
export function setReasoningFixes(next: ReasoningFixes): void {
  fixes = next;
  void saveJsonSetting(KEY, fixes);
}

/** Live view for the settings UI (Providers → learned reasoning fixes). */
export const allReasoningFixes = (): ReasoningFixes => fixes;

/** Forget one learned answer — the next request sends what the user configured and re-probes. */
export function clearReasoningFix(model: string): void {
  if (!(model in fixes)) return;
  fixes = Object.fromEntries(Object.entries(fixes).filter(([k]) => k !== model));
  void saveJsonSetting(KEY, fixes);
}
